import type { StationPhase } from '../../figures/LineFigures.tsx'

import type { MonitoringDeviceKey } from './monitoringModel.ts'

/**
 * **장비 카드의 공정 진행 줄 — 서버 모듈 상태의 순서.** 지금은 6축 로봇만 줄이 있다.
 *
 * | 장비 | 줄 |
 * | --- | --- |
 * | 6축 로봇 | `MOVING → HANDOFF` (dev2 2026-10-06 `robot6-1` `module_state_change` 32건 실측 — 전이는 `IDLE ↔ MOVING` · `MOVING ↔ HANDOFF` 뿐) |
 *
 * ⚠ 원심분리기 · 어셈블러 · 염색기의 줄(2026-10-06 cdb2f49)은 2026-10-07 에 걷었다(eric 「원심분리기 어셈블러 염색기에.. 이것들
 *    없애줘」). 표에 장비가 없으면 줄이 서지 않는다(`ProcessSteps` 가 `null`) — 세 장비 항목을 되살리지 마라.
 *    어셈블러 압착 · UV 칸의 「진행 중」 표시(`activeBlockOf`)는 별개라 남는다.
 *
 * 🔴 **«지금»은 서버가 준 `state` 하나로만 고른다.** 줄에 없는 상태(`IDLE` 등)면 아무 칸도 켜지 않는다 —
 *    직전 칸을 짐작해 남기지 않는다.
 * 🔴 이름은 사전 `enum.moduleState.*`(서버 어휘) 그대로다. 새 말을 짓지 않는다.
 */
export const PROCESS_STEPS: Readonly<Partial<Record<MonitoringDeviceKey, readonly string[]>>> = {
  /* ⚠ 6축 줄(「이동 중 → 핸드오프」 · 2026-10-06)도 2026-10-08 에 걷었다(eric 「6축 로봇에 이동 중 핸드오프 내용도 빼줘」) — 가로 라인이 그 사실을 모양으로 말한다 */
}

/**
 * 상태 → 지금 일하는 장치 블록. 블록 키는 장비 판 그림 격자의 것이다(`press` 는 어셈블러 압착부 · `mod` 는 UV 쪽 본체).
 * 🔴 표에 없는 상태면 `undefined` — 어느 장치도 「진행 중」이 아니다.
 */
const ACTIVE_BLOCK: Readonly<
  Partial<Record<MonitoringDeviceKey, Readonly<Record<string, string>>>>
> = {
  cent: { RUNNING: 'cent' },
  asm: { TC_LOAD: 'press', TC_REMOVE: 'press', UV: 'mod' },
  stain: { SHAKING: 'shaker', INCUBATING: 'shaker' },
}

/** 지금 칸 — 줄에 든 상태일 때만 그 상태. 🔴 순수 함수 */
export function currentProcessStep(
  device: MonitoringDeviceKey,
  state: string | undefined,
  stale: boolean,
): string | undefined {
  if (stale || state === undefined) return undefined
  return PROCESS_STEPS[device]?.includes(state) === true ? state : undefined
}

/** 지금 일하는 장치 블록 키. 🔴 순수 함수 — 끊긴 화면(`stale`)은 «지금»을 말하지 않는다 */
export function activeBlockOf(
  device: MonitoringDeviceKey,
  state: string | undefined,
  stale: boolean,
): string | undefined {
  if (stale || state === undefined) return undefined
  return ACTIVE_BLOCK[device]?.[state]
}

/** 어셈블러 상태 → 장치 그림의 지금(`AssemblerStationLine`). 표에 없는 상태 · 끊긴 화면이면 `idle` */
const STATION_PHASE: Readonly<Record<string, StationPhase>> = {
  TC_LOAD: 'load',
  UV: 'uv',
  TC_REMOVE: 'remove',
}

/**
 * 어셈블러 장치 그림의 지금 — 🔴 순수 함수 · 서버 상태 하나로만(eric 2026-10-07 「동영상 형태로」 — 움직임은 이 값의 변화다).
 * 끊긴 화면(`stale`)은 «지금»을 말하지 않는다(헤드가 올라간 그림).
 */
export function stationPhaseOf(state: string | undefined, stale: boolean): StationPhase {
  if (stale || state === undefined) return 'idle'
  return STATION_PHASE[state] ?? 'idle'
}
